import { useRef, useLayoutEffect, RefObject, DependencyList } from "react";

interface Position {
  x: number;
  y: number;
}

interface ScrollPositionOptions {
  element?: RefObject<HTMLElement>;
  useWindow?: boolean;
  boundingElement?: RefObject<HTMLElement>;
}

interface ScrollPositionCallback {
  prevPos: Position;
  currPos: Position;
}

type ScrollPositionCallbackFn = (params: ScrollPositionCallback) => void;

const isBrowser: boolean = typeof window !== `undefined`;
const zeroPosition: Position = { x: 0, y: 0 };

const getClientRect = (element: Element | null): DOMRect | undefined =>
  element?.getBoundingClientRect();

const getScrollPosition = ({
  element,
  useWindow,
  boundingElement,
}: ScrollPositionOptions): Position => {
  if (!isBrowser) {
    return zeroPosition;
  }

  if (useWindow) {
    return { x: window.scrollX, y: window.scrollY };
  }

  const targetPosition = getClientRect(element?.current || document.body);
  const containerPosition = getClientRect(boundingElement?.current || null);

  if (!targetPosition) {
    return zeroPosition;
  }

  return containerPosition
    ? {
        x: (containerPosition.x || 0) - (targetPosition.x || 0),
        y: (containerPosition.y || 0) - (targetPosition.y || 0),
      }
    : { x: targetPosition.left, y: targetPosition.top };
};

export const useScrollPosition = (
  cb: ScrollPositionCallbackFn,
  deps: DependencyList = [],
  element?: RefObject<HTMLElement>,
  useWindow: boolean = false,
  wait: number = 100,
  boundingElement?: RefObject<HTMLElement>,
): void => {
  const position = useRef(getScrollPosition({ useWindow, boundingElement }));

  let throttleTimeout: ReturnType<typeof setTimeout> | null = null;

  const callBack = (): void => {
    const currPos = getScrollPosition({ element, useWindow, boundingElement });
    cb({ prevPos: position.current, currPos });
    position.current = currPos;
    throttleTimeout = null;
  };

  useLayoutEffect(() => {
    if (!isBrowser) {
      return undefined;
    }

    const handleScroll = () => {
      if (wait) {
        if (throttleTimeout === null) {
          throttleTimeout = setTimeout(callBack, wait);
        }
      } else {
        callBack();
      }
    };

    if (boundingElement && boundingElement.current) {
      boundingElement.current.addEventListener("scroll", handleScroll, {
        passive: true,
      });
    } else {
      window.addEventListener("scroll", handleScroll, { passive: true });
    }

    return () => {
      if (boundingElement && boundingElement.current) {
        boundingElement.current.removeEventListener("scroll", handleScroll);
      } else {
        window.removeEventListener("scroll", handleScroll);
      }

      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, deps);
};

// Default values are now handled in the function signature
